// Building blocks of the branded chat page (pages/BrandPage.jsx). Every
// colour here is a CSS variable from lib/brandPageTheme.js, set on the page
// root — never the site's own theme tokens — so the page is the business's,
// not jawabai's. Layout follows the Claude Design "Branded Chat Page" canvas.
import { useState } from 'react';
import { brandImageUrl } from '../../lib/brandPageApi';
import { WEEK_ORDER, dayName, formatTime } from '../../lib/openingHours';

const ICONS = {
  whatsapp:
    'M7.9 20A9 9 0 1 0 4 16.1L2 22Z M9.2 8.6c.3-.6.9-.6 1.2 0l.6 1.3c.1.3 0 .6-.2.8l-.5.5c.6 1.2 1.5 2.1 2.7 2.7l.5-.5c.2-.2.5-.3.8-.2l1.3.6c.6.3.6.9 0 1.2-1.8 1.2-4.2.2-5.8-1.5-1.6-1.6-2.6-3.9-1.6-4.9Z',
  instagram:
    'M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5z M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z M17.5 6.5h.01',
  facebook: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z',
  phone:
    'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z',
  website:
    'M12 2a10 10 0 1 0 0 20a10 10 0 1 0 0-20z M2 12h20 M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z',
  pin: 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z M12 7a3 3 0 1 0 0 6a3 3 0 1 0 0-6z',
  clock: 'M12 2a10 10 0 1 0 0 20a10 10 0 1 0 0-20z M12 6v6l4 2',
  send: 'M22 2 11 13 M22 2l-7 20-4-9-9-4 20-7z',
  arrowOut: 'M7 17 17 7 M8 7h9v9',
};

export function Icon({ name, size = 20, stroke = 1.9, className = '', style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden="true"
    >
      <path d={ICONS[name]} />
    </svg>
  );
}

// The business's logo, or the first letter of its name on the brand colour.
// A logo that fails to load falls back to the letter rather than a broken image.
export function Avatar({ page, size, ring = false, t }) {
  const [failed, setFailed] = useState(false);
  const src = !failed && brandImageUrl(page.logoImageId);
  const initial = (page.name || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <div
      className="grid shrink-0 place-items-center overflow-hidden rounded-full bg-[var(--brand)] font-extrabold text-[var(--brand-fg)]"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.42),
        ...(ring
          ? {
              border: '4px solid var(--surface)',
              boxShadow: '0 8px 20px -8px rgba(0,0,0,.3)',
            }
          : null),
      }}
    >
      {src ? (
        <img
          src={src}
          alt={t('logoAlt', { name: page.name })}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="leading-none" aria-hidden="true">
          {initial}
        </span>
      )}
    </div>
  );
}

// Cover photo, or a gradient of the page colours when there is none.
export function Hero({ page, height, gradientHeight }) {
  const [failed, setFailed] = useState(false);
  const src = !failed && brandImageUrl(page.coverImageId);
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="block w-full object-cover"
        style={{ height }}
        onError={() => setFailed(true)}
      />
    );
  }
  return <div style={{ height: gradientHeight, background: 'var(--hero-grad)' }} />;
}

// Only the links the business filled in, in a fixed order.
export function contactLinks(page, t) {
  const l = page.links || {};
  const out = [];
  if (l.whatsapp)
    out.push({
      key: 'whatsapp',
      href: `https://wa.me/${l.whatsapp.replace(/\D/g, '')}`,
    });
  if (l.instagram)
    out.push({
      key: 'instagram',
      href: `https://instagram.com/${encodeURIComponent(l.instagram)}`,
    });
  if (l.facebook) out.push({ key: 'facebook', href: l.facebook });
  if (l.phone) out.push({ key: 'phone', href: `tel:${l.phone}` });
  if (l.website) out.push({ key: 'website', href: l.website });
  return out.map((x) => ({ ...x, label: t(`links.${x.key}`) }));
}

export function LinkButtons({ links, size = 46, surface = 'var(--surface)' }) {
  if (!links.length) return null;
  return (
    <div className="flex flex-wrap justify-center gap-2.5">
      {links.map((l) => (
        <a
          key={l.key}
          href={l.href}
          aria-label={l.label}
          title={l.label}
          {...(l.key === 'phone' ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
          className="grid place-items-center rounded-full border border-[var(--border)] text-[var(--brand-ink)] shadow-[0_2px_6px_-3px_rgba(0,0,0,.15)] transition-colors hover:border-[var(--brand)] hover:bg-[var(--brand)] hover:text-[var(--brand-fg)]"
          style={{ width: size, height: size, background: surface }}
        >
          <Icon name={l.key} size={Math.round(size * 0.43)} />
        </a>
      ))}
    </div>
  );
}

function IconTile({ name, size = 34 }) {
  return (
    <div
      className="grid shrink-0 place-items-center rounded-[10px] bg-[var(--brand-tint)] text-[var(--brand-ink)]"
      style={{ width: size, height: size }}
    >
      <Icon name={name} size={size / 2} stroke={2} />
    </div>
  );
}

// "9:00 AM – 5:00 PM" in the page's direction. Each time is isolated (<bdi>)
// so an Arabic "9:00 ص" cannot have its digits and AM/PM marker reordered by
// its neighbours.
export function TimeRange({ from, to, lang }) {
  return (
    <>
      <bdi>{formatTime(from, lang)}</bdi> – <bdi>{formatTime(to, lang)}</bdi>
    </>
  );
}

export function OpenBadge({ open, t }) {
  return (
    <span
      className={`flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${
        open
          ? 'bg-[var(--accent-tint)] text-[var(--accent-ink)]'
          : 'bg-[var(--surface-2)] text-[var(--text-muted)]'
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {open ? t('openNow') : t('closedNow')}
    </span>
  );
}

function MapsLink({ href, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex shrink-0 items-center gap-1 font-bold text-[var(--brand-ink)] hover:underline"
    >
      {label}
      <Icon name="arrowOut" size={13} stroke={2.4} className="rtl:-scale-x-100" />
    </a>
  );
}

// Mobile: one compact card with a row each for location and today's hours.
export function InfoCardCompact({ page, hours, t, lang }) {
  const hasLoc = !!(page.address || page.mapsUrl);
  if (!hasLoc && !hours.has) return null;
  return (
    <div className="mx-4 mt-4 overflow-hidden rounded-[18px] border border-[var(--border)] bg-[var(--surface)]">
      {hasLoc && (
        <div className="flex items-center gap-3 px-3.5 py-3">
          <IconTile name="pin" />
          <div dir="auto" className="min-w-0 flex-1 text-[13.5px] leading-snug">
            {page.address || t('location')}
          </div>
          {page.mapsUrl && (
            <span className="text-[13px]">
              <MapsLink href={page.mapsUrl} label={t('mapsShort')} />
            </span>
          )}
        </div>
      )}
      {hasLoc && hours.has && <div className="ms-[60px] h-px bg-[var(--border)]" />}
      {hours.has && (
        <div className="flex items-center gap-3 px-3.5 py-3">
          <IconTile name="clock" />
          {/* On a narrow phone the badge wraps under the hours rather than
              squeezing them; each time never breaks inside itself. */}
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
            <div className="min-w-0 text-[13.5px] leading-snug text-[var(--text-muted)]">
              {hours.today?.open
                ? t('today', {
                    // U+2068/U+2069 isolate each time inside the sentence, as
                    // <bdi> does in TimeRange; U+00A0 keeps "10:00 ص" whole.
                    from: `\u2068${unbreakable(formatTime(hours.today.opens, lang))}\u2069`,
                    to: `\u2068${unbreakable(formatTime(hours.today.closes, lang))}\u2069`,
                  })
                : t('closedToday')}
            </div>
            <OpenBadge open={hours.openNow} t={t} />
          </div>
        </div>
      )}
    </div>
  );
}

function unbreakable(text) {
  return text.replace(/ /g, '\u00a0');
}

// Desktop: location and the full week inside the profile card.
export function InfoSections({ page, hours, t, lang }) {
  const hasLoc = !!(page.address || page.mapsUrl);
  return (
    <>
      {hasLoc && (
        <div className="flex gap-3 border-t border-[var(--border)] px-[22px] py-4">
          <IconTile name="pin" size={36} />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-xs font-semibold text-[var(--text-muted)]">{t('location')}</span>
            {page.address && (
              <span dir="auto" className="text-sm leading-normal">
                {page.address}
              </span>
            )}
            {page.mapsUrl && (
              <span className="text-[13.5px]">
                <MapsLink href={page.mapsUrl} label={t('maps')} />
              </span>
            )}
          </div>
        </div>
      )}
      {hours.has && (
        <div className="flex gap-3 border-t border-[var(--border)] px-[22px] py-4">
          <IconTile name="clock" size={36} />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-[var(--text-muted)]">{t('hours')}</span>
              <OpenBadge open={hours.openNow} t={t} />
            </div>
            {WEEK_ORDER.map((day) => {
              const d = hours.week.find((h) => h.day === day);
              const isToday = day === hours.todayIndex;
              return (
                <div
                  key={day}
                  className={`flex justify-between gap-3 text-[13.5px] leading-normal ${isToday ? 'font-bold' : ''}`}
                >
                  <span className={isToday ? '' : 'text-[var(--text-muted)]'}>
                    {dayName(day, lang)}
                  </span>
                  <span className={isToday ? '' : 'font-medium'}>
                    {d?.open ? <TimeRange from={d.opens} to={d.closes} lang={lang} /> : t('closed')}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

export function PoweredBy({ show, className = '', t }) {
  if (!show) return null;
  return (
    <a
      href="https://jawabai.cloud"
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-1.5 text-[var(--text-muted)] hover:underline ${className}`}
    >
      <span className="grid h-[13px] w-[13px] place-items-center rounded bg-[var(--text-muted)] text-[9px] font-extrabold leading-none text-[var(--surface)]">
        j
      </span>
      {/* Follows the page's language and direction; only the name keeps
          the brand's Latin font. */}
      {t('poweredBy')}{' '}
      <b dir="ltr" className="font-['Plus_Jakarta_Sans',sans-serif] font-bold text-[var(--text)]">
        jawabai
      </b>
    </a>
  );
}

// ─────────────────────────────────────────
// Chat
// ─────────────────────────────────────────

function BotBubble({ page, t, children, desktop }) {
  return (
    <div className="bp-in flex items-end gap-2">
      <Avatar page={page} size={desktop ? 30 : 28} t={t} />
      <div
        className={`whitespace-pre-wrap break-words rounded-[18px] rounded-es-[6px] border border-[var(--border)] bg-[var(--surface)] shadow-[0_1px_2px_rgba(0,0,0,.04)] ${
          desktop
            ? 'max-w-[72%] px-4 py-3 text-[15.5px] leading-relaxed'
            : 'max-w-[80%] px-3.5 py-[11px] text-[15px] leading-normal'
        }`}
      >
        {/* The bubble keeps the page's direction (its tail points at the
            avatar); the text inside takes its own: Arabic on an English page
            reads right-to-left, English on an Arabic page left-to-right. */}
        <div dir="auto">{children}</div>
      </div>
    </div>
  );
}

export function Messages({
  page,
  messages,
  typing,
  t,
  desktop,
  onChip,
  showInlineChips,
  replySuggestions = [],
}) {
  return (
    <>
      <BotBubble page={page} t={t} desktop={desktop}>
        {page.welcomeMessage || t('welcome')}
      </BotBubble>
      {showInlineChips && (
        <div className={`flex flex-wrap gap-2 ${desktop ? 'ps-10' : 'ps-9'}`}>
          {page.suggestedQuestions.map((q) => (
            <Chip key={q} onClick={() => onChip(q)}>
              {q}
            </Chip>
          ))}
        </div>
      )}
      {messages.map((m, i) =>
        m.who === 'me' ? (
          <div
            key={i}
            className={`bp-in self-end whitespace-pre-wrap break-words rounded-[18px] rounded-ee-[6px] bg-[var(--brand)] text-[var(--brand-fg)] ${
              desktop
                ? 'max-w-[72%] px-4 py-[11px] text-[15.5px] leading-relaxed'
                : 'max-w-[80%] px-3.5 py-2.5 text-[15px] leading-normal'
            }`}
          >
            <div dir="auto">{m.text}</div>
          </div>
        ) : m.who === 'error' ? (
          <div
            key={i}
            role="alert"
            className="bp-in self-center rounded-xl bg-[var(--surface-2)] px-3.5 py-2 text-center text-[13.5px] text-[var(--text-muted)]"
          >
            {m.text}
          </div>
        ) : (
          <BotBubble key={i} page={page} t={t} desktop={desktop}>
            {m.text}
          </BotBubble>
        ),
      )}
      {replySuggestions.length > 0 && (
        <div className={`flex flex-wrap gap-2 ${desktop ? 'ps-10' : 'ps-9'}`}>
          {replySuggestions.map((q) => (
            <Chip key={q} onClick={() => onChip(q)} label={t('askChip', { question: q })}>
              {q}
            </Chip>
          ))}
        </div>
      )}
      {typing && (
        <BotBubble page={page} t={t} desktop={desktop}>
          <span className="flex gap-1 py-1" aria-label="…">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="bp-dot h-1.5 w-1.5 rounded-full bg-[var(--text-muted)]"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </span>
        </BotBubble>
      )}
    </>
  );
}

export function Chip({ children, onClick, strip = false, label }) {
  return (
    <button
      type="button"
      dir="auto"
      aria-label={label}
      onClick={onClick}
      className={`rounded-full border border-[var(--brand-line)] bg-[var(--surface)] font-semibold text-[var(--brand-ink)] transition-colors hover:bg-[var(--brand-tint)] ${
        strip
          ? 'shrink-0 whitespace-nowrap px-3 py-[7px] text-[13px]'
          : 'px-[13px] py-2 text-start text-[13.5px]'
      }`}
    >
      {children}
    </button>
  );
}

// maxLength: the admin-set limit the page API sends (chat-service enforces it).
export function Composer({ value, onChange, onSubmit, disabled, placeholder, maxLength = 1000, t, lang }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="flex items-center gap-2"
    >
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        placeholder={placeholder}
        aria-label={placeholder}
        // Typing follows the text (Arabic right-to-left, English left-to-right);
        // empty, the box and its placeholder follow the page — "auto" with
        // nothing typed would fall back to left-to-right on an Arabic page.
        dir={value ? 'auto' : lang === 'ar' ? 'rtl' : 'ltr'}
        autoComplete="off"
        enterKeyHint="send"
        className="bp-field h-[46px] min-w-0 flex-1 rounded-full border border-[var(--border)] bg-[var(--surface-2)] px-4 text-base text-[var(--text)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--brand)]"
      />
      <button
        type="submit"
        disabled={disabled || !value.trim()}
        aria-label={t('send')}
        className="grid h-[46px] w-[46px] shrink-0 place-items-center rounded-full bg-[var(--brand)] text-[var(--brand-fg)] shadow-[0_6px_14px_-6px_var(--brand)] transition active:scale-95 disabled:opacity-50"
      >
        <Icon
          name="send"
          size={20}
          stroke={2.2}
          style={lang === 'ar' ? { transform: 'scaleX(-1)' } : undefined}
        />
      </button>
    </form>
  );
}
